import { PreloaderCircular } from '@shared/ui/kit/preloader-circular/index';
import { DropdownProps } from '@shared/ui/kit/dropdown/model/types';
import useClickOutside from '@shared/hooks/useClickOutside';
import ChevronDownIcon from './assets/ChevronDownIcon';
import useDropdownLogic from '../lib/useDropdownLogic';
import { XStylesSettings } from '@shared/types/types';
import { XmarkIcon } from './assets/x-mark-icon';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';
import './styles.css';

const Dropdown = <T,>(props: DropdownProps<T>) => {
  const {
    placeholder,
    isDisabled,
    selectType = 'default',
    isSearch = false,
    size = 'small',
    settings,
    itemText,
    labelMessage = 'Открыть *',
    isLoading = false,
  } = props;

  const {
    isOpenOptions,
    toggleIsOpen,
    inputChangeHandler,
    setIsOpenOptions,
    displayedOptions,
    toggleSelectOptionHandler,
    checkIsSelectedOption,
    getSelectIcon,
    inputStopPrevent,
    inputRef,
    inputValue,
    selectedOptions,
    clearSelectedValues,
    cleanInputHandler,
    clickOutsideHandler,
  } = useDropdownLogic(props);

  useClickOutside(inputRef, () => {
    setIsOpenOptions(false);
    clickOutsideHandler();
  });

  return (
    <div {...stylex.props(styles.block)}>
      {labelMessage && (
        <div {...stylex.props(styles.aboveDropdown)}>
          <div {...stylex.props(styles.labelMessage(isDisabled, size))}>{labelMessage}</div>
        </div>
      )}
      <div
        ref={inputRef}
        data-testid="container"
        {...stylex.props(styles.container, styles[size], settings as XStylesSettings)}
      >
        <input
          {...stylex.props(
            styles.base,
            styles.inputText(isSearch, size),
            (isDisabled || isLoading) && styles.isDisabled
          )}
          disabled={isDisabled || isLoading}
          placeholder={placeholder}
          onChange={inputChangeHandler}
          value={`${inputValue}`}
          onFocus={(e) => {
            inputStopPrevent(e, isSearch);
            cleanInputHandler();
          }}
          onMouseDown={(e) => inputStopPrevent(e, isSearch)}
          onClick={() => {
            toggleIsOpen();
          }}
        />
        <div {...stylex.props(styles.optionsBlock)}>
          {isLoading ? (
            <div {...stylex.props(styles.preloader(size))} data-testid="preloader">
              <PreloaderCircular size={size} variant="secondary" />
            </div>
          ) : selectedOptions.length > 0 ? (
            <div
              data-testid="xmark-icon"
              {...stylex.props(styles.arrowIcon(size, isDisabled))}
              onClick={clearSelectedValues}
            >
              <XmarkIcon size={size} />
            </div>
          ) : (
            <div data-testid="arrow-icon" {...stylex.props(styles.arrowIcon(size, isDisabled))} onClick={toggleIsOpen}>
              <ChevronDownIcon isOpenOptions={isOpenOptions} size={size} />
            </div>
          )}

          {isOpenOptions && displayedOptions ? (
            <div data-testid="options" {...stylex.props(styles.options)}>
              <div className="custom-scroll">
                {isSearch && displayedOptions?.length === 0 ? (
                  <div {...stylex.props(styles.noResultsFound)}>Результат не найден.</div>
                ) : (
                  displayedOptions?.map((option, index) => (
                    <div
                      onClick={() => toggleSelectOptionHandler(option)}
                      key={index}
                      {...stylex.props(styles.optionsItem(checkIsSelectedOption(option), selectType))}
                    >
                      <div {...stylex.props(styles.optionTitle)}>{String(option[itemText])}</div>
                      <div data-testid="option-icon" {...stylex.props(styles.optionIcon)}>
                        {selectType ? getSelectIcon(selectType, checkIsSelectedOption(option)) : ''}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            ''
          )}
        </div>
      </div>
    </div>
  );
};

export default Dropdown;
